/**
 * OpenAI Realtime turn detection configuration.
 *
 * Mirrors the OpenAI Realtime API surface 1:1 — every value here is a value the
 * API accepts. No translation layer, no coercion, no lies.
 *
 * Two modes via discriminated union on `type`:
 *   - `server_vad`     — pure audio-level voice activity detection
 *   - `semantic_vad`   — model-based turn detection with `eagerness` knob
 *
 * @see https://platform.openai.com/docs/guides/realtime#voice-activity-detection
 */

import { z } from "zod";

// ============================================================
// Enums (verbatim API values)
// ============================================================

/** OpenAI Realtime semantic_vad eagerness — natively supports three levels. */
export const OPENAI_SEMANTIC_VAD_EAGERNESS_LEVELS = ["low", "medium", "high"] as const;
export type OpenAIRealtimeEagerness = (typeof OPENAI_SEMANTIC_VAD_EAGERNESS_LEVELS)[number];

/** Telephony noise reduction modes — mapped to OpenAI's input_audio_noise_reduction.type. */
export const OPENAI_NOISE_REDUCTION_MODES = ["near_field", "far_field"] as const;
export type OpenAINoiseReductionMode = (typeof OPENAI_NOISE_REDUCTION_MODES)[number];

// ============================================================
// Schema
// ============================================================

/**
 * server_vad branch — audio-level VAD.
 *
 * Field naming follows the OpenAI API (snake_case). Renaming requires both an API
 * contract change and a JSON migration; not worth the churn.
 */
const ServerVadBranchSchema = z.object({
  type: z.literal("server_vad"),
  /** Voice detection sensitivity (0.0–1.0). Higher = requires louder audio. */
  threshold: z.number().min(0).max(1),
  /** Audio to include before detected speech start (ms). */
  prefix_padding_ms: z.number().min(0).max(2000),
  /** Silence duration before treating speech as done (ms). */
  silence_duration_ms: z.number().min(100).max(5000)
});

/**
 * semantic_vad branch — model-based turn detection.
 *
 * `eagerness` is OpenAI-native: low / medium / high. All three are first-class
 * values the API accepts. This is the only provider with a true 3-level enum.
 */
const SemanticVadBranchSchema = z.object({
  type: z.literal("semantic_vad"),
  eagerness: z.enum(OPENAI_SEMANTIC_VAD_EAGERNESS_LEVELS)
});

/**
 * OpenAI Realtime turn detection — discriminated by `type`.
 *
 * Shared knobs (interrupt_response, noise_reduction) live alongside the discriminant
 * because they apply to both branches.
 */
export const OpenAIRealtimeTurnDetectionSchema = z
  .discriminatedUnion("type", [ServerVadBranchSchema, SemanticVadBranchSchema])
  .and(
    z.object({
      /** Interrupt the AI when the user starts speaking. */
      interrupt_response: z.boolean(),
      /** Telephony noise preprocessing. null = disabled. */
      noise_reduction: z.enum(OPENAI_NOISE_REDUCTION_MODES).nullable()
    })
  );

export type OpenAIRealtimeTurnDetection = z.infer<typeof OpenAIRealtimeTurnDetectionSchema>;

// ============================================================
// Defaults
// ============================================================

/**
 * Default turn detection for new OpenAI Realtime voice deployments.
 *
 * server_vad with conservative defaults — works for most telephony scenarios.
 * Customers tune eagerness via the UI when they want semantic behavior.
 */
export const DEFAULT_OPENAI_REALTIME_TURN_DETECTION: OpenAIRealtimeTurnDetection = {
  type: "server_vad",
  threshold: 0.8,
  prefix_padding_ms: 300,
  silence_duration_ms: 500,
  interrupt_response: true,
  noise_reduction: "far_field"
};
